import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  Loader2,
  Paintbrush,
  Layers,
  Clock,
  X,
  LayoutTemplate,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Card } from '../../ui/card';
import { Input } from '../../ui/input';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '../../ui/select';
import { CanvasEditor } from './CanvasEditor';
import { feedWatchService } from '../../../services/feedWatch';
import type { CanvasDesign, CanvasElement } from '../../../services/feedWatch';
import type { BreadcrumbSegment } from '../../canvas-shared/CanvasBreadcrumb';
import { CANVAS_TEMPLATES, TEMPLATE_CATEGORIES, type CanvasTemplate } from './canvasTemplates';

// ── Create Dialog ────────────────────────────────────────────

interface CreateDialogProps {
  isOpen: boolean;
  isCreating: boolean;
  onClose: () => void;
  onCreate: (name: string, width: number, height: number, elements?: CanvasElement[]) => void;
}

const DIMENSION_PRESETS = [
  { label: 'Google Shopping (1200x628)', width: 1200, height: 628 },
  { label: 'Instagram Post (1080x1080)', width: 1080, height: 1080 },
  { label: 'Instagram Story (1080x1920)', width: 1080, height: 1920 },
  { label: 'Custom', width: 0, height: 0 },
];

function CreateDialog({ isOpen, isCreating, onClose, onCreate }: CreateDialogProps) {
  const [name, setName] = useState('');
  const [presetIndex, setPresetIndex] = useState(0);
  const [customWidth, setCustomWidth] = useState(800);
  const [customHeight, setCustomHeight] = useState(600);
  const [selectedTemplate, setSelectedTemplate] = useState<CanvasTemplate | null>(null);
  const [activeCategory, setActiveCategory] = useState<CanvasTemplate['category'] | 'blank'>('shopping');

  if (!isOpen) return null;

  const usingTemplate = selectedTemplate !== null;
  const finalWidth = usingTemplate
    ? selectedTemplate.width
    : DIMENSION_PRESETS[presetIndex].width === 0
      ? customWidth
      : DIMENSION_PRESETS[presetIndex].width;
  const finalHeight = usingTemplate
    ? selectedTemplate.height
    : DIMENSION_PRESETS[presetIndex].height === 0
      ? customHeight
      : DIMENSION_PRESETS[presetIndex].height;
  const canCreate = name.trim() && finalWidth > 0 && finalHeight > 0;

  const handleSelectTemplate = (template: CanvasTemplate | null) => {
    setSelectedTemplate(template);
    if (template) {
      setName(template.name);
    } else {
      setName('');
    }
  };

  const filteredTemplates = activeCategory === 'blank'
    ? []
    : CANVAS_TEMPLATES.filter((t) => t.category === activeCategory);

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-6">
      <Card className="w-full max-w-3xl border-2 shadow-2xl rounded-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b shrink-0">
          <div>
            <h2 className="text-lg font-semibold">New Design</h2>
            <p className="text-sm text-muted-foreground mt-0.5">Pick a template or start blank</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 flex flex-col p-5 gap-4 overflow-y-auto">
          {/* Category tabs */}
          <div className="flex items-center gap-1 p-1 bg-muted/50 rounded-xl shrink-0">
            <button
              onClick={() => { setActiveCategory('blank'); handleSelectTemplate(null); }}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-150 ${
                activeCategory === 'blank'
                  ? 'bg-background text-foreground shadow-sm border'
                  : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
              }`}
            >
              Blank
            </button>
            {TEMPLATE_CATEGORIES.map((cat) => (
              <button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-150 ${
                  activeCategory === cat.key
                    ? 'bg-background text-foreground shadow-sm border'
                    : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* Template grid or blank canvas options */}
          {activeCategory === 'blank' ? (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Dimensions</label>
                <Select
                  value={String(presetIndex)}
                  onValueChange={(v) => setPresetIndex(Number(v))}
                >
                  <SelectTrigger className="h-10 w-full text-sm rounded-lg">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DIMENSION_PRESETS.map((preset, i) => (
                      <SelectItem key={i} value={String(i)}>{preset.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {DIMENSION_PRESETS[presetIndex].width === 0 && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">Width (px)</label>
                    <Input
                      type="number"
                      value={customWidth}
                      onChange={(e) => setCustomWidth(Number(e.target.value))}
                      className="h-10 rounded-lg"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">Height (px)</label>
                    <Input
                      type="number"
                      value={customHeight}
                      onChange={(e) => setCustomHeight(Number(e.target.value))}
                      className="h-10 rounded-lg"
                    />
                  </div>
                </div>
              )}
              {DIMENSION_PRESETS[presetIndex].width !== 0 && (
                <p className="text-sm text-muted-foreground">
                  {DIMENSION_PRESETS[presetIndex].width} x {DIMENSION_PRESETS[presetIndex].height} px
                </p>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5">
              {filteredTemplates.map((tpl) => {
                const isSelected = selectedTemplate?.name === tpl.name;
                const isVertical = tpl.height > tpl.width;
                return (
                  <button
                    key={tpl.name}
                    onClick={() => handleSelectTemplate(tpl)}
                    className={`
                      group p-3 rounded-xl border-2 text-left transition-all duration-150 flex flex-col items-center gap-2
                      ${isSelected ? 'border-primary bg-primary/5 shadow-md' : 'border-border hover:border-primary/40 hover:bg-muted/30'}
                    `}
                  >
                    {/* Aspect ratio thumbnail */}
                    <div
                      className={`rounded-lg border border-border/60 bg-muted/40 ${isSelected ? 'border-primary/30' : ''}`}
                      style={{
                        width: isVertical ? 36 : 56,
                        height: isVertical ? 56 : 36,
                      }}
                    />
                    <div className="w-full text-center">
                      <div className="text-xs font-semibold truncate">{tpl.name}</div>
                      <div className="text-[10px] text-muted-foreground mt-0.5">{tpl.description}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Name input — always visible */}
          <div className="space-y-1.5 shrink-0">
            <label className="text-sm font-medium">Design Name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={usingTemplate ? selectedTemplate.name : 'My Design'}
              className="h-10 rounded-lg"
            />
          </div>

          {usingTemplate && (
            <p className="text-xs text-muted-foreground shrink-0">
              {selectedTemplate.width} x {selectedTemplate.height} px &middot; {selectedTemplate.elements.length} pre-placed elements
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-5 border-t shrink-0">
          <Button variant="outline" onClick={onClose} className="rounded-lg">Cancel</Button>
          <Button
            onClick={() => onCreate(
              name.trim(),
              finalWidth,
              finalHeight,
              usingTemplate ? selectedTemplate.elements : undefined,
            )}
            disabled={!canCreate || isCreating}
            className="rounded-lg"
          >
            {isCreating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
            Create
          </Button>
        </div>
      </Card>
    </div>
  );
}

// ── Design Card ──────────────────────────────────────────────

function DesignCard({ design, onClick }: { design: CanvasDesign; onClick: () => void }) {
  return (
    <Card
      className="border-2 shadow-lg rounded-xl p-5 cursor-pointer hover:border-primary/50 hover:shadow-xl transition-all duration-200"
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-semibold text-base">{design.name}</h3>
          <p className="text-sm text-muted-foreground mt-1">
            {design.width} x {design.height} px
          </p>
        </div>
        <Paintbrush className="h-5 w-5 text-muted-foreground" />
      </div>
      <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <Layers className="h-3.5 w-3.5" />
          {design.elements.length} element{design.elements.length !== 1 ? 's' : ''}
        </span>
        <span className="flex items-center gap-1">
          <Clock className="h-3.5 w-3.5" />
          {new Date(design.updated_at).toLocaleDateString()}
        </span>
      </div>
    </Card>
  );
}

// ── Manager ──────────────────────────────────────────────────

interface CanvasManagerProps {
  feedId: string;
  detectedFields: string[];
  totalProducts?: number;
  breadcrumbSegments?: BreadcrumbSegment[];
}

export function CanvasManager({ feedId, detectedFields, totalProducts, breadcrumbSegments }: CanvasManagerProps) {
  const [designs, setDesigns] = useState<CanvasDesign[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [editingDesign, setEditingDesign] = useState<CanvasDesign | null>(null);

  const loadDesigns = useCallback(async () => {
    try {
      setIsLoading(true);
      const result = await feedWatchService.listDesigns(feedId);
      setDesigns(Array.isArray(result) ? result : []);
    } catch {
      // handled
    } finally {
      setIsLoading(false);
    }
  }, [feedId]);

  useEffect(() => {
    loadDesigns();
  }, [loadDesigns]);

  const handleCreate = async (name: string, width: number, height: number, elements?: CanvasElement[]) => {
    try {
      setIsCreating(true);
      const templateId = `tpl_${name.toLowerCase().replace(/\s+/g, '_')}_${Date.now()}`;
      const design = await feedWatchService.createDesign(feedId, {
        name,
        template_id: templateId,
        width,
        height,
        elements: elements ?? [],
      });
      setDesigns((prev) => [...prev, design]);
      setIsDialogOpen(false);
      setEditingDesign(design);
    } catch {
      // handled
    } finally {
      setIsCreating(false);
    }
  };

  const handleDesignUpdated = (updated: CanvasDesign) => {
    setDesigns((prev) => prev.map((d) => (d.id === updated.id ? updated : d)));
    setEditingDesign(updated);
  };

  // If editing a design, show the editor
  if (editingDesign) {
    return (
      <CanvasEditor
        feedId={feedId}
        design={editingDesign}
        detectedFields={detectedFields}
        totalProducts={totalProducts}
        onBack={() => setEditingDesign(null)}
        onDesignUpdated={handleDesignUpdated}
        breadcrumbSegments={[
          ...(breadcrumbSegments ?? []),
          { label: 'Designs', onClick: () => setEditingDesign(null) },
        ]}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Canvas Designs</h2>
          <p className="text-sm text-muted-foreground">
            Create visual templates and render branded product images at scale.
          </p>
        </div>
        <Button onClick={() => setIsDialogOpen(true)} className="rounded-lg">
          <Plus className="h-4 w-4 mr-2" />
          New Design
        </Button>
      </div>

      {/* Design list */}
      {designs.length === 0 ? (
        <Card className="border-2 border-dashed rounded-xl p-12 text-center">
          <Paintbrush className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-lg font-medium text-muted-foreground">No designs yet</p>
          <p className="text-sm text-muted-foreground mt-1">Create a design to get started with visual templates.</p>
          <Button onClick={() => setIsDialogOpen(true)} className="rounded-lg mt-4">
            <Plus className="h-4 w-4 mr-2" />
            Create First Design
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {designs.map((design) => (
            <DesignCard
              key={design.id}
              design={design}
              onClick={() => setEditingDesign(design)}
            />
          ))}
        </div>
      )}

      <CreateDialog
        isOpen={isDialogOpen}
        isCreating={isCreating}
        onClose={() => setIsDialogOpen(false)}
        onCreate={handleCreate}
      />
    </div>
  );
}
